import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import type { EngagementEvent } from "@/lib/engagement";

export interface Recipient {
  person_id: number;
  first_name: string;
  last_name: string | null;
  email: string;
  gender: "female" | "male" | "other" | null;
  home_city: string | null;
  home_country: string | null;
  quizrun_count: number;
  matched_by: string;
}

export interface EventOption {
  id: number;
  label: string;
}

export interface MessageSummary {
  id: string;
  title: string;
  subject: string;
  body_markdown: string;
  category: string;
  status: string;
  audience: unknown;
  created_at: string;
  updated_at: string;
  queued_at: string | null;
  completed_at: string | null;
  queued_count: number;
  sending_count: number;
  sent_count: number;
  failed_count: number;
}

export interface MessageRecipient {
  queue_id: string;
  person_id: number | null;
  recipient_name: string | null;
  email: string;
  status: string;
  attempts: number;
  created_at: string;
  updated_at: string;
  sent_at: string | null;
  last_error: string | null;
  latest_type: string | null;
  latest_at: string | null;
  click_count: number;
  events: EngagementEvent[] | null;
  unsubscribed_at: string | null;
}

export interface EmailPreview {
  preview_id: string;
  message_id: string | null;
  email: string;
  subject: string;
  created_at: string;
  latest_type: string | null;
  latest_at: string | null;
  click_count: number;
  events: EngagementEvent[] | null;
}

interface MessageResult {
  success: boolean;
  messageId?: string;
  queued?: number;
  sent?: number;
  failed?: number;
  skipped?: number;
  retried?: number;
  error?: string;
}

export async function runMessageAction(
  body: Record<string, unknown>,
): Promise<MessageResult> {
  const { data, error } = await supabase.functions.invoke("email-message", {
    body,
  });
  if (error) return { success: false, error: error.message };
  return data as MessageResult;
}

export function listMessages() {
  return supabase.rpc("list_email_messages");
}

export function listPreviews() {
  return supabase.rpc("get_email_previews", { p_limit: 20 });
}

export function getMessageRecipients(messageId: string) {
  return supabase.rpc("get_email_message_recipients", {
    p_message_id: messageId,
  });
}

export async function getMessageLocations(
  isAdmin: boolean,
  hostLocationIds: number[],
) {
  let query = supabase.from("location").select("id, country").order("country");
  if (!isAdmin) query = query.in("id", hostLocationIds);
  return Promise.all([query, supabase.rpc("list_message_home_locations")]);
}

export async function getMessageEvents(
  selectedQuizrunId: number,
  isAdmin: boolean,
  hostLocationIds: number[],
) {
  let query = supabase
    .from("event")
    .select("id, location_id, location:location_id(city)")
    .eq("quizrun_id", selectedQuizrunId)
    .order("date_start", { ascending: false });
  if (!isAdmin) query = query.in("location_id", hostLocationIds);
  return await query;
}

export function resolveMessageRecipients(
  args: Database["public"]["Functions"]["resolve_blast_recipients"]["Args"],
) {
  return supabase.rpc("resolve_blast_recipients", args);
}

export function uploadMessageImage(path: string, file: File) {
  return supabase.storage
    .from("message-images")
    .upload(path, file, { contentType: file.type });
}

export function getMessageImageUrl(path: string) {
  return supabase.storage.from("message-images").getPublicUrl(path);
}

export function fetchEmailUsageStats() {
  return supabase.rpc("email_usage_stats");
}

export type EventEmailJob =
  Database["public"]["Functions"]["admin_list_event_email_jobs"]["Returns"][number];

export function listEventEmailJobs() {
  return supabase.rpc("admin_list_event_email_jobs");
}

type EmailLogRow =
  Database["public"]["Functions"]["admin_list_email_log"]["Returns"][number];

/** Generated types mark every column non-null; these are null per source. */
export type EmailLogGroup = Omit<
  EmailLogRow,
  "event_id" | "kind" | "category" | "subject" | "day" | "quizrun_title" | "city"
> & {
  event_id: number | null;
  kind: string | null;
  category: string | null;
  subject: string | null;
  day: string | null;
  quizrun_title: string | null;
  city: string | null;
};

export const EMAIL_LOG_LIMIT = 100;

/** Automatic, transactional and other queue mail outside Messaging messages, grouped. */
export function listEmailLog() {
  return supabase.rpc("admin_list_email_log", { p_limit: EMAIL_LOG_LIMIT });
}

/**
 * One email log group: an event + kind (narrowed to one event_email_job when
 * jobId is set), or a category + subject + UTC day.
 */
export type EmailLogGroupKey =
  | { eventId: number | null; kind: string; jobId?: number }
  | { category: string; subject: string; day: string };

export async function listEmailLogRecipients(
  group: EmailLogGroupKey,
): Promise<MessageRecipient[]> {
  const args =
    "kind" in group
      ? {
          p_event_id: group.eventId ?? undefined,
          p_kind: group.kind,
          p_job_id: group.jobId,
        }
      : { p_category: group.category, p_subject: group.subject, p_day: group.day };
  const { data, error } = await supabase.rpc("admin_list_email_log_recipients", args);
  if (error) throw error;
  return (data ?? []) as unknown as MessageRecipient[];
}

export function cancelEventEmailJob(jobId: number) {
  return supabase.rpc("admin_cancel_event_email_job", { p_job_id: jobId });
}

/** Move an invite/reminder to another moment (YYYY-MM-DDTHH:MM, event local time). */
export function rescheduleEventEmailJob(jobId: number, at: string) {
  return supabase.rpc("admin_reschedule_event_email_job", {
    p_job_id: jobId,
    p_at: at,
  });
}

/** Master switch for automatic event emails (email_quota_config.event_emails_enabled). */
export function getEventEmailsEnabled() {
  return supabase.rpc("admin_get_event_emails_enabled");
}

export function setEventEmailsEnabled(enabled: boolean) {
  return supabase.rpc("admin_set_event_emails_enabled", { p_enabled: enabled });
}

type InvokeBody = NonNullable<
  NonNullable<Parameters<typeof supabase.functions.invoke>[1]>["body"]
>;

export function invokeSendEmail(body: InvokeBody) {
  return supabase.functions.invoke("send-email", {
    body,
  });
}

export function invokeUnsubscribe(email: string, token: string) {
  return supabase.functions.invoke("unsubscribe", {
    body: { email, token },
  });
}

/** "Not this time": skip the remaining invites/reminders for one quizrun. */
export function invokeEventDecline(
  quizrunId: number,
  email: string,
  token: string,
) {
  return supabase.functions.invoke<{ ok: boolean; title: string | null }>(
    "event-decline",
    {
      body: { quizrunId, email, token },
    },
  );
}
